import { Callout, Expander, Heading, HorizontalRule } from '@piggybank/core';
import { Form, FormFeedback, Navigation, WizardPage } from '@piggybank/form';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import T from 'prop-types';
import React from 'react';
import { MarkdownTranslation } from './Markdown';
import {
  FormContext,
  NavigationContextProvider,
  Optional,
  Translation,
  TranslationScope,
  useTranslations,
} from './context';
import { getInitialFormValues } from './operators';
import {
  localizationKeyPath,
  pruneUnwantedPropertiesForPage,
  requiredArg,
} from './utils';
import { createFormValidator } from './validation';

export const handleNextInEditMode = ({
  context = requiredArg('context'),
  formValues = requiredArg('formValues'),
  getErrors = requiredArg('getErrors'),
  navigateTo = requiredArg('navigateTo'),
}) => {
  const errors = get(getErrors(formValues), 'errors', {});

  if (isEmpty(errors)) {
    return navigateTo(context.lastPageIndex, formValues);
  }

  return null;
};

export const DynamicFormPage = ({
  context = requiredArg('context'),
  validate = requiredArg('validate'),
  renderChildren = requiredArg('renderChildren'),
  getMetadataForFieldName = requiredArg('getMetadataForFieldName'),
  getFormStructure = requiredArg('getFormStructure'),
  setPageLevelErrors = requiredArg('setPageLevelErrors'),
  getSectionsForPageName = requiredArg('getSectionsForPageName'),
  getSortOrderForFieldNames = requiredArg('getSortOrderForFieldNames'),
  pageConfig,
  bindSubmitForm,
  showNavigation = true,
}) => {
  const { pageName } = context.keyPath;

  const initialValues = {
    [pageName]: getInitialFormValues(validate.schema.properties[pageName]),
  };

  return (
    <WizardPage key={pageName} name={pageName}>
      {({ navigateTo }) => {
        const translations = useTranslations(); // eslint-disable-line

        const getErrors = createFormValidator({
          validate,
          pageName,
          translations,
          getMetadataForFieldName,
          getFormStructure,
          getSectionsForPageName,
          getSortOrderForFieldNames,
        });

        return (
          <NavigationContextProvider navigateTo={navigateTo}>
            <FormContext.Consumer>
              {({ review: { inReviewEdit } = {} }) => (
                <Form
                  initialValues={initialValues}
                  name={context.formName || 'dynamic-form'}
                  onSubmit={(contextOnSubmit, { values: formValues }) => {
                    const prunedFormValues = pruneUnwantedPropertiesForPage({
                      pageName,
                      formValues,
                      getFormStructure,
                    });

                    return contextOnSubmit({
                      values: prunedFormValues,
                    });
                  }}
                  validate={values => {
                    const { errors, messages } = getErrors(values);

                    setPageLevelErrors(errors);

                    return messages;
                  }}
                >
                  {({ resetForm, submitForm, values: formValues }) => {
                    if (window.Cypress || global.__DEV__) {
                      window.formikResetForm = resetForm;
                      window.formikValues = formValues;
                    }

                    if (bindSubmitForm) {
                      bindSubmitForm(submitForm);
                    }

                    return (
                      <TranslationScope path={pageName}>
                        <Optional>
                          <Heading level={1}>
                            <MarkdownTranslation
                              formValues={formValues}
                              path="heading"
                            />
                          </Heading>
                        </Optional>
                        <Optional>
                          <MarkdownTranslation
                            formValues={formValues}
                            marginBottom={4}
                            path="description"
                          />
                        </Optional>
                        <Optional>
                          <Translation path="expander">
                            {({ heading }) => (
                              <>
                                <HorizontalRule />
                                <Expander
                                  id={`${localizationKeyPath(
                                    context.keyPath
                                  ).join('-')}-expander`}
                                  marginBottom={4}
                                  title={heading}
                                >
                                  <MarkdownTranslation
                                    formValues={formValues}
                                    path="expander.content"
                                  />
                                </Expander>
                              </>
                            )}
                          </Translation>
                        </Optional>
                        <Optional>
                          <Translation path="callout">
                            {({ heading, type = 'information' }) => (
                              <Callout type={type}>
                                <Heading level={4} marginBottom={1}>
                                  {heading}
                                </Heading>
                                <MarkdownTranslation
                                  formValues={formValues}
                                  path="callout.content"
                                />
                              </Callout>
                            )}
                          </Translation>
                        </Optional>
                        {global.__DEBUG__ ? (
                          <pre>
                            {JSON.stringify(
                              { formValues, errors: getErrors(formValues) },
                              null,
                              2
                            )}
                          </pre>
                        ) : null}
                        <FormFeedback
                          data-testid={`formfeedback-${pageName}`}
                        />
                        {renderChildren({
                          context,
                          formValues,
                        })}
                        {showNavigation && (
                          <Translation
                            absolutePath="global.navigation"
                            fallbackValue={{
                              save: 'Save',
                              next: 'Continue',
                              previous: 'Back',
                              submit: 'Submit',
                            }}
                          >
                            {({ next, save, ...buttonTextMap }) => {
                              const disablePrevious = get(
                                pageConfig,
                                'navigation.disablePrevious',
                                inReviewEdit
                              );

                              return (
                                <Navigation
                                  disablePrevious={disablePrevious}
                                  onPrevious={(contextOnPrevious, values) => {
                                    const prunedFormValues =
                                      pruneUnwantedPropertiesForPage({
                                        pageName,
                                        formValues: values,
                                        getFormStructure,
                                      });

                                    return contextOnPrevious(prunedFormValues);
                                  }}
                                  textMap={{
                                    ...buttonTextMap,
                                    next: inReviewEdit ? save : next,
                                  }}
                                  {...(inReviewEdit
                                    ? {
                                        onNext() {
                                          const prunedFormValues =
                                            pruneUnwantedPropertiesForPage({
                                              pageName,
                                              formValues,
                                              getFormStructure,
                                            });

                                          return handleNextInEditMode({
                                            context,
                                            formValues: prunedFormValues,
                                            getErrors,
                                            navigateTo,
                                          });
                                        },
                                      }
                                    : {})}
                                />
                              );
                            }}
                          </Translation>
                        )}
                      </TranslationScope>
                    );
                  }}
                </Form>
              )}
            </FormContext.Consumer>
          </NavigationContextProvider>
        );
      }}
    </WizardPage>
  );
};

DynamicFormPage.propTypes = {
  context: T.shape({
    keyPath: T.object,
    schemaPath: T.arrayOf(T.string),
  }),
  validate: T.oneOfType([T.func, T.object]),
  renderChildren: T.func,
  getMetadataForFieldName: T.func,
  schemaProperties: T.object,
  getFormStructure: T.func,
  setPageLevelErrors: T.func,
  getSectionsForPageName: T.func,
  getSortOrderForFieldNames: T.func,
  pageConfig: T.object,
  bindSubmitForm: T.func,
  showNavigation: T.bool,
};
